<template>
  <div>
    <!-- Componente de Notificações -->
    <div class="notification-container" ref="notificationContainer">
      <!-- Ícone de Sino -->
      <button 
        @click="toggleNotificationPanel"
        class="notification-bell-button"
        :class="[
          hasUnreadNotifications ? 'has-unread' : '',
          !props.areNotificationsEnabled ? 'notification-disabled' : ''
        ]"
        :disabled="!props.areNotificationsEnabled"
        aria-label="Notificações"
      >
        <div class="notification-bell-wrapper">
          <Bell class="notification-bell-icon" />
          <Lock v-if="!props.areNotificationsEnabled" class="notification-lock-icon" />
          <RefreshCw v-if="loading && firstWebSocketConnect" class="notification-bell-loading" />
          <span 
            v-if="hasUnreadNotifications" 
            class="notification-badge"
            aria-label="Notificações não lidas"
          >
            {{ unreadCount > 9 ? '9+' : unreadCount }}
          </span>
        </div>
      </button>

      <!-- Painel de Notificações (Popup) -->
      <transition name="fade">
        <div 
          v-if="isNotificationPanelOpen" 
          class="notification-panel"
          :class="{ 'notification-panel-mobile': isMobile }"
        >
          <div class="notification-header">
            <h3 class="notification-title">Notificações</h3>
            <button 
              @click="markAllAsRead" 
              class="notification-action-button"
              :disabled="unreadCount === 0"
            >
              Marcar todas como lidas
            </button>
          </div>

          <!-- Abas de Notificações -->
          <div class="notification-tabs">
            <button 
              @click="activeTab = 'unread'" 
              class="notification-tab"
              :class="{ 'active': activeTab === 'unread' }"
              aria-label="Mostrar notificações não lidas"
            >
              Não lidas 
              <span class="notification-tab-count">{{ unreadCount }}</span>
            </button>
            <button 
              @click="showReadNotifications" 
              class="notification-tab"
              :class="{ 'active': activeTab === 'read' }"
              aria-label="Mostrar notificações lidas"
            >
              Lidas 
            </button>
          </div>

          <!-- Lista de Notificações -->
          <div class="notification-list-container">
            <div v-if="loading" class="notification-loading">
              <RefreshCw class="notification-loading-icon" />
              <span>Carregando notificações...</span>
            </div>
            
            <div v-else-if="filteredNotifications.length === 0" class="notification-empty">
              <div class="notification-empty-icon">
                <Bell class="h-8 w-8 text-gray-300 dark:text-gray-600" />
              </div>
              <p>Nenhuma notificação {{ activeTab === 'unread' ? 'não lida' : 'lida' }}</p>
            </div>
            
            <div v-else class="notification-list">
              <div 
                v-for="notification in filteredNotifications" 
                :key="notification.id"
                class="notification-item"
                :class="{ 'unread': !notification.read }"
                @click="handleNotificationClick(notification)"
              >
                <!-- Ícone da Notificação -->
                <div class="notification-icon" :class="`notification-icon-${getNotificationType(notification.type)}`">
                  <component 
                    :is="getNotificationIcon(notification.type)" 
                    class="h-5 w-5"
                  />
                </div>
                
                <!-- Conteúdo da Notificação -->
                <div class="notification-content">
                  <div class="notification-item-header">
                    <h4 class="notification-item-title">
                      {{ notification.title }}
                    </h4>
                    <div class="notification-meta">
                      <span class="notification-time">
                        {{ formatTime(notification.time) }}
                      </span>
                      <span 
                        v-if="!notification.read" 
                        class="notification-unread-indicator"
                      ></span>
                    </div>
                  </div>
                  <p class="notification-message">
                    {{ truncateMessage(notification.message) }}
                  </p>
                  <button 
                    v-if="notification.message.length > 60"
                    @click.stop="openMessageModal(notification)"
                    class="notification-view-more"
                  >
                    Ver mais
                  </button>
                </div>
                <!-- Botão "Marcar como lida" -->
                <button 
                  v-if="!notification.read"
                  class="notification-mark-read-btn notification-mark-read-btn-small"
                  @click.stop="markAsRead(notification)"
                >
                  Marcar como lida
                </button>
              </div>
            </div>
          </div>

          <!-- Botão de Histórico Completo -->
          <div class="notification-footer">
            <button 
              @click="openFullHistory" 
              class="notification-history-button"
            >
              <History class="h-4 w-4 mr-2" />
              Histórico completo
            </button>
          </div>
        </div>
      </transition>
    </div>

    <!-- Página de Histórico Completo -->
    <transition name="modal">
      <div 
        v-if="showFullHistory" 
        class="notification-history-modal"
        role="dialog"
        aria-labelledby="history-title"
      >
        <!-- Overlay com blur -->
        <div 
          class="notification-modal-overlay"
          @click="showFullHistory = false"
        ></div>
        
        <!-- Conteúdo do histórico -->
        <div class="notification-history-content">
          <div class="notification-history-header">
            <h2 id="history-title" class="notification-history-title">Histórico de Notificações</h2>
            <button 
              @click="showFullHistory = false" 
              class="notification-close-button"
              aria-label="Fechar histórico"
            >
              <X class="h-6 w-6" />
            </button>
          </div>

          <div class="notification-history-body">
            <!-- Filtros e Pesquisa -->
            <div class="notification-filters">
              <div class="notification-search">
                <Search class="notification-search-icon" />
                <input 
                  v-model="searchQuery" 
                  type="text" 
                  placeholder="Pesquisar notificações..." 
                  class="notification-search-input"
                />
              </div>
              
              <div class="notification-filter-controls">
                <select 
                  v-model="filterType" 
                  class="notification-filter-select"
                  aria-label="Filtrar por tipo"
                >
                  <option value="all">Todos os tipos</option>
                  <option value="comentario_evento">Comentários</option>
                  <option value="manutencao">Manutenção</option>
                  <option value="update">Atualizações</option>
                  <option value="feature">Novidades</option>
                </select>
                
                <select 
                  v-model="filterStatus" 
                  class="notification-filter-select"
                  aria-label="Filtrar por status"
                >
                  <option value="all">Todos os status</option>
                  <option value="read">Lidas</option>
                  <option value="unread">Não lidas</option>
                </select>
              </div>
            </div>

            <!-- Lista de Todas as Notificações -->
            <div class="notification-history-list-container">
              <div v-if="loading" class="notification-loading">
                <RefreshCw class="notification-loading-icon" />
                <span>Carregando notificações...</span>
              </div>
              
              <div v-else-if="filteredHistoryNotifications.length === 0" class="notification-empty">
                <div class="notification-empty-icon">
                  <Search class="h-8 w-8 text-gray-300 dark:text-gray-600" />
                </div>
                <p>Nenhuma notificação encontrada</p>
              </div>
              
              <div v-else class="notification-history-list">
                <div 
                  v-for="notification in filteredHistoryNotifications" 
                  :key="notification.id"
                  class="notification-history-item"
                  :class="{ 'unread': !notification.read }"
                >
                  <div class="notification-history-item-content">
                    <div class="notification-icon" :class="`notification-icon-${getNotificationType(notification.type)}`">
                      <component 
                        :is="getNotificationIcon(notification.type)" 
                        class="h-6 w-6"
                      />
                    </div>
                    
                    <div class="notification-history-details">
                      <div class="notification-history-item-header">
                        <h4 class="notification-history-item-title">
                          {{ notification.title }}
                          <span 
                            v-if="!notification.read" 
                            class="notification-unread-indicator"
                          ></span>
                        </h4>
                        <span class="notification-history-date">
                          {{ formatDate(notification.time) }}
                        </span>
                      </div>
                      <p class="notification-history-message">
                        {{ truncateMessage(notification.message, 100) }}
                      </p>
                      <div class="notification-history-actions">
                        <button 
                          v-if="notification.message.length > 100"
                          @click.stop="openMessageModal(notification)" 
                          class="notification-view-more"
                        >
                          Ver mais
                        </button>
                        <button 
                          v-if="!notification.read"
                          @click.stop="markAsRead(notification)" 
                          class="notification-mark-read"
                        >
                          Marcar como lida
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Modal para exibir mensagem completa -->
    <transition name="modal">
      <div 
        v-if="showMessageModal" 
        class="notification-message-modal"
        role="dialog"
        aria-labelledby="message-title"
      >
        <!-- Overlay com blur -->
        <div 
          class="notification-modal-overlay"
          @click="showMessageModal = false"
        ></div>
        
        <!-- Conteúdo do modal -->
        <div class="notification-message-content">
          <div class="notification-message-header">
            <h3 id="message-title" class="notification-message-title">
              {{ selectedNotification?.title }}
            </h3>
            <button 
              @click="showMessageModal = false" 
              class="notification-close-button"
              aria-label="Fechar mensagem"
            >
              <X class="h-5 w-5" />
            </button>
          </div>
          
          <div class="notification-message-body">
            {{ selectedNotification?.message }}
          </div>
          
          <div class="notification-message-footer">
            <span class="notification-message-time">
              {{ selectedNotification ? formatDate(selectedNotification.time) : '' }}
            </span>
            <div class="notification-message-type" v-if="selectedNotification">
              <component 
                :is="getNotificationIcon(selectedNotification.type)" 
                class="h-4 w-4 mr-1"
              />
              <span>{{ getNotificationTypeName(selectedNotification.type) }}</span>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Overlay para fechar o painel quando clicar fora -->
    <div 
      v-if="isNotificationPanelOpen" 
      class="notification-panel-overlay"
      @click="closeNotificationPanel"
    ></div>
  </div>
</template>

<script setup>
import { onMounted, onBeforeUnmount, ref, computed, defineProps} from 'vue'
import { useRouter } from 'vue-router'
import { useNotificationStore } from '@/stores/notifications/useNotificationStore'
import { useClinicStore } from '@/stores/settings/clinicStore'
import {
  Bell, X, History, Search, RefreshCw, Zap, Clock, Lock,
  Gauge, MessageSquare, AlertTriangle, Info
} from 'lucide-vue-next'

const router = useRouter()
// 📦 Store
const notificationStore = useNotificationStore()
const clinicStore = useClinicStore()
clinicStore.initialize()
// 🧠 Estado
const isNotificationPanelOpen = ref(false)
const activeTab = ref('unread')
const showFullHistory = ref(false)
const showMessageModal = ref(false)
const searchQuery = ref('')
const filterType = ref('all')
const filterStatus = ref('all')
const notificationContainer = ref(null)
const isMobile = ref(false)
const selectedNotification = ref(null)

// 🚀 Computeds
const notifications = computed(() => notificationStore.notifications)
const unreadCount = computed(() => notifications.value.filter(n => !n.read).length)
const hasUnreadNotifications = computed(() => unreadCount.value > 0)
const loading = computed(() => notificationStore.loading)
const firstWebSocketConnect = computed(() => notificationStore.firstWebSocketConnect)

// 📦 Marcar como lida
const markAsRead = (notification) => {
  notificationStore.markAsRead(notification)
}

const markAllAsRead = () => {
  notificationStore.markAllAsRead()
}

// 🔍 Filtros e Histórico
const filteredNotifications = computed(() =>
  notifications.value
    .filter(n => activeTab.value === 'unread' ? !n.read : n.read)
    .sort((a, b) => b.time - a.time)
)

const filteredHistoryNotifications = computed(() => {
  let result = [...notifications.value]
  if (filterStatus.value === 'read') result = result.filter(n => n.read)
  else if (filterStatus.value === 'unread') result = result.filter(n => !n.read)
  if (filterType.value !== 'all') result = result.filter(n => n.type === filterType.value)
  if (searchQuery.value) {
    const q = searchQuery.value.toLowerCase()
    result = result.filter(n =>
      n.title.toLowerCase().includes(q) ||
      n.message.toLowerCase().includes(q)
    )
  }
  return result.sort((a, b) => b.time - a.time)
})

// 📦 Panel
const toggleNotificationPanel = () => {
  isNotificationPanelOpen.value = !isNotificationPanelOpen.value
  if (isNotificationPanelOpen.value) {
    if (unreadCount.value > 0) activeTab.value = 'unread'
  }
}

const closeNotificationPanel = (e) => {
  if (notificationContainer.value && !notificationContainer.value.contains(e.target)) {
    isNotificationPanelOpen.value = false
  }
}

const openFullHistory = () => {
  isNotificationPanelOpen.value = false
  showFullHistory.value = true
  document.body.classList.add('overflow-hidden')
}

// 📩 Ações
const openMessageModal = (notification) => {
  selectedNotification.value = notification
  showMessageModal.value = true
  if (!notification.read) markAsRead(notification)
}

const handleNotificationClick = async (notification) => {
  if (!notification.read) {
    await markAsRead(notification)
  }
  if (notification.link) {
    if (notification.link.startsWith('/')) router.push(notification.link)
    else window.location.href = notification.link
  } else {
    openMessageModal(notification)
  }
}

// 🛠️ Helpers
const truncateMessage = (message, length = 60) => {
  if (!message) return ''
  return message.length > length ? message.substring(0, length) + '...' : message
}

const getNotificationType = (type) => {
  switch (type) {
    case 'update': return 'update'
    case 'feature': return 'feature'
    case 'maintenance':
    case 'manutencao': return 'maintenance'
    case 'performance': return 'performance'
    case 'comentario_evento': return 'comment'
    case 'error':
    case 'warning': return 'warning'
    default: return 'default'
  }
}

const getNotificationTypeName = (type) => {
  switch (type) {
    case 'update': return 'Atualização'
    case 'feature': return 'Novidade'
    case 'maintenance':
    case 'manutencao': return 'Manutenção'
    case 'performance': return 'Desempenho'
    case 'comentario_evento': return 'Comentário'
    case 'error':
    case 'warning': return 'Alerta'
    default: return 'Notificação'
  }
}

const getNotificationIcon = (type) => {
  switch (type) {
    case 'update': return RefreshCw
    case 'feature': return Zap
    case 'maintenance':
    case 'manutencao': return Clock
    case 'performance': return Gauge
    case 'comentario_evento': return MessageSquare
    case 'error':
    case 'warning': return AlertTriangle
    case 'info': return Info
    default: return Bell
  }
}

const props = defineProps({
  areNotificationsEnabled: {
    type: Boolean,
    default: true
  }
})

const formatTime = (date) => {
  const now = new Date()
  const diff = Math.floor((now - date) / 1000)
  if (diff < 60) return `${diff}s`
  if (diff < 3600) return `${Math.floor(diff / 60)}m`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`
  return formatDate(date, true)
}

const formatDate = (date, short = false) => {
  if (!date) return ''
  // Se não for Date, tenta converter
  const d = (date instanceof Date) ? date : new Date(date)
  if (isNaN(d)) return ''
  if (short) return d.toLocaleDateString('pt-BR')
  return d.toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit',
    year: 'numeric', hour: '2-digit', minute: '2-digit'
  })
}

// 🧠 Responsivo
const checkScreenSize = () => {
  isMobile.value = window.innerWidth < 768
}

// 🚀 Lifecycle
onMounted(() => {
  window.addEventListener('resize', checkScreenSize)
  checkScreenSize()
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', checkScreenSize)
  document.body.classList.remove('overflow-hidden')
})

const showReadNotifications = () => {
  if (notificationStore.socket && notificationStore.socket.readyState === 1) {
    notificationStore.socket.send(JSON.stringify({ action: 'get_read' }))
  }
  activeTab.value = 'read'
}
</script>




<style scoped>
/* Estilos base */
.notification-container {
  position: relative;
  display: inline-block;
}

.notification-bell-button {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border-radius: 9999px;
  transition: all 0.2s ease;
  background: transparent;
  border: none;
  cursor: pointer;
}

.notification-bell-button:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.dark .notification-bell-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.notification-bell-button.has-unread {
  color: #3b82f6;
}

.notification-bell-icon {
  width: 1.5rem;
  height: 1.5rem;
  color: var(--clinic-icon-color) !important;
  stroke: var(--clinic-icon-color) !important;
  fill: none;
}

.notification-badge {
  position: absolute;
  top: -0.25rem;
  right: -0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 1rem;
  height: 1rem;
  padding: 0 0.25rem;
  font-size: 0.625rem;
  font-weight: 700;
  color: white;
  background-color: #ef4444;
  border-radius: 9999px;
}

/* Painel de notificações */
.notification-panel {
  position: absolute;
  right: 0;
  top: 100%;
  width: 20rem;
  max-height: 32rem;
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.1);
  z-index: 50;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.dark .notification-panel {
  background-color: #1f2937;
  border-color: rgba(255, 255, 255, 0.1);
}

.notification-panel-mobile {
  position: fixed;
  left: 1rem;
  right: 1rem;
  top: 4rem;
  width: auto;
}

.notification-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.dark .notification-header {
  border-color: rgba(255, 255, 255, 0.1);
}

.notification-title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
}

.dark .notification-title {
  color: white;
}

.notification-action-button {
  font-size: 0.875rem;
  color: #3b82f6;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  transition: background-color 0.2s;
}

.notification-action-button:hover {
  background-color: rgba(59, 130, 246, 0.1);
}

.notification-action-button:disabled {
  color: #9ca3af;
  cursor: not-allowed;
}

/* Abas */
.notification-tabs {
  display: flex;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.dark .notification-tabs {
  border-color: rgba(255, 255, 255, 0.1);
}

.notification-tab {
  flex: 1;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  text-align: center;
  color: #6b7280;
  background: none;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
}

.dark .notification-tab {
  color: #9ca3af;
}

.notification-tab.active {
  color: #3b82f6;
  border-bottom: 2px solid #3b82f6;
}

.notification-tab-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.5rem;
  height: 1.5rem;
  padding: 0 0.375rem;
  margin-left: 0.375rem;
  font-size: 0.75rem;
  background-color: #f3f4f6;
  border-radius: 9999px;
}

.dark .notification-tab-count {
  background-color: #374151;
}

/* Lista de notificações */
.notification-list-container {
  flex: 1;
  overflow-y: auto;
  max-height: 20rem;
}

.notification-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: #6b7280;
}

.dark .notification-loading {
  color: #9ca3af;
}

.notification-loading-icon {
  width: 1.5rem;
  height: 1.5rem;
  margin-bottom: 0.5rem;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.notification-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: #6b7280;
  text-align: center;
}

.dark .notification-empty {
  color: #9ca3af;
}

.notification-empty-icon {
  margin-bottom: 1rem;
}

.notification-list {
  padding: 0.5rem 0;
}

.notification-item {
  display: flex;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: background-color 0.2s;
}

.notification-item:last-child {
  border-bottom: none;
}

.notification-item:hover {
  background-color: #f9fafb;
}

.dark .notification-item:hover {
  background-color: #1e293b;
}

.notification-item.unread {
  background-color: #f0f9ff;
}

.dark .notification-item.unread {
  background-color: rgba(59, 130, 246, 0.1);
}

.notification-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  margin-right: 0.75rem;
  border-radius: 9999px;
  flex-shrink: 0;
}

.notification-icon-default {
  background-color: #e5e7eb;
  color: #6b7280;
}

.notification-icon-update {
  background-color: #dbeafe;
  color: #3b82f6;
}

.notification-icon-feature {
  background-color: #fef3c7;
  color: #d97706;
}

.notification-icon-maintenance {
  background-color: #e0e7ff;
  color: #6366f1;
}

.notification-icon-performance {
  background-color: #dcfce7;
  color: #10b981;
}

.notification-icon-comment {
  background-color: #f3e8ff;
  color: #8b5cf6;
}

.notification-icon-warning {
  background-color: #fee2e2;
  color: #ef4444;
}

.dark .notification-icon-default {
  background-color: #374151;
  color: #9ca3af;
}

.dark .notification-icon-update {
  background-color: rgba(59, 130, 246, 0.2);
  color: #60a5fa;
}

.dark .notification-icon-feature {
  background-color: rgba(217, 119, 6, 0.2);
  color: #fbbf24;
}

.dark .notification-icon-maintenance {
  background-color: rgba(99, 102, 241, 0.2);
  color: #818cf8;
}

.dark .notification-icon-performance {
  background-color: rgba(16, 185, 129, 0.2);
  color: #34d399;
}

.dark .notification-icon-comment {
  background-color: rgba(139, 92, 246, 0.2);
  color: #a78bfa;
}

.dark .notification-icon-warning {
  background-color: rgba(239, 68, 68, 0.2);
  color: #f87171;
}

.notification-content {
  flex: 1;
  min-width: 0;
}

.notification-item-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.25rem;
}

.notification-item-title {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 500;
  color: #111827;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dark .notification-item-title {
  color: white;
}

.notification-meta {
  display: flex;
  align-items: center;
  margin-left: 0.5rem;
}

.notification-time {
  font-size: 0.75rem;
  color: #6b7280;
  white-space: nowrap;
}

.dark .notification-time {
  color: #9ca3af;
}

.notification-unread-indicator {
  width: 0.5rem;
  height: 0.5rem;
  margin-left: 0.5rem;
  background-color: #ef4444;
  border-radius: 9999px;
}

.notification-message {
  font-size: 0.75rem;
  color: #4b5563;
  margin: 0;
  line-height: 1.4;
}

.dark .notification-message {
  color: #d1d5db;
}

.notification-view-more {
  font-size: 0.75rem;
  color: #3b82f6;
  background: none;
  border: none;
  padding: 0;
  margin-top: 0.25rem;
  cursor: pointer;
}

.notification-view-more:hover {
  text-decoration: underline;
}

.notification-bell-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.notification-lock-icon {
  position: absolute;
  top: -0.4rem;
  right: -0.4rem;
  width: 1rem;
  height: 1rem;
  background-color: #fee2e2;
  border-radius: 9999px;
  padding: 0.15rem;
  color: #dc2626;
  z-index: 10;
}
@keyframes pulse-lock {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.7;
  }
}

.notification-lock-icon {
  position: absolute;
  top: -0.4rem;
  right: -0.4rem;
  width: 1rem;
  height: 1rem;
  background-color: #fee2e2;
  border-radius: 9999px;
  padding: 0.15rem;
  color: #dc2626;
  z-index: 10;
  animation: pulse-lock 1.8s infinite ease-in-out;
}



/* Rodapé */
.notification-footer {
  padding: 0.75rem;
  background-color: #f9fafb;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
}

.dark .notification-footer {
  background-color: #1f2937;
  border-color: rgba(255, 255, 255, 0.1);
}

.notification-history-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: white;
  background-color: #3b82f6;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

.notification-history-button:hover {
  background-color: #2563eb;
}

/* Modal de histórico */
.notification-history-modal {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
}

.notification-modal-overlay {
  position: absolute;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
}

.notification-history-content {
  position: relative;
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  width: 100%;
  max-width: 64rem;
  max-height: 90vh;
  overflow: hidden;
  margin: 1rem;
  display: flex;
  flex-direction: column;
}

.dark .notification-history-content {
  background-color: #1f2937;
}

.notification-history-header {
  position: sticky;
  top: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background-color: white;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  z-index: 10;
}

.dark .notification-history-header {
  background-color: #1f2937;
  border-color: rgba(255, 255, 255, 0.1);
}

.notification-history-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
}

.dark .notification-history-title {
  color: white;
}

.notification-close-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 9999px;
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  transition: background-color 0.2s;
}

.notification-close-button:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.dark .notification-close-button {
  color: #9ca3af;
}

.dark .notification-close-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.notification-history-body {
  flex: 1;
  overflow: auto;
  padding: 1rem;
}

.notification-filters {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

@media (min-width: 640px) {
  .notification-filters {
    flex-direction: row;
  }
}

.notification-search {
  position: relative;
  flex: 1;
}

.notification-search-icon {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  width: 1.25rem;
  height: 1.25rem;
  color: #9ca3af;
}

.notification-search-input {
  width: 100%;
  padding: 0.5rem 1rem 0.5rem 2.5rem;
  font-size: 0.875rem;
  color: #111827;
  background-color: white;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
}

.dark .notification-search-input {
  color: white;
  background-color: #374151;
  border-color: #4b5563;
}

.notification-filter-controls {
  display: flex;
  gap: 0.5rem;
}

.notification-filter-select {
  padding: 0.5rem;
  font-size: 0.875rem;
  color: #111827;
  background-color: white;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
}

.dark .notification-filter-select {
  color: white;
  background-color: #374151;
  border-color: #4b5563;
}

.notification-history-list-container {
  border-radius: 0.375rem;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.notification-history-list {
  background-color: white;
}

.dark .notification-history-list {
  background-color: #1f2937;
}

.notification-history-item {
  padding: 1rem;
  border-bottom: 1px solid #e5e7eb;
}

.dark .notification-history-item {
  border-color: #374151;
}

.notification-history-item:last-child {
  border-bottom: none;
}

.notification-history-item:hover {
  background-color: #f9fafb;
}

.dark .notification-history-item:hover {
  background-color: #1e293b;
}

.notification-history-item.unread {
  background-color: #f0f9ff;
}

.dark .notification-history-item.unread {
  background-color: rgba(59, 130, 246, 0.1);
}

.notification-history-item-content {
  display: flex;
  gap: 1rem;
}

.notification-history-details {
  flex: 1;
  min-width: 0;
}

.notification-history-item-header {
  display: flex;
  flex-direction: column;
  margin-bottom: 0.5rem;
}

@media (min-width: 640px) {
  .notification-history-item-header {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
}

.notification-history-item-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 500;
  color: #111827;
}

.dark .notification-history-item-title {
  color: white;
}

.notification-history-date {
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.25rem;
}

@media (min-width: 640px) {
  .notification-history-date {
    margin-top: 0;
  }
}

.dark .notification-history-date {
  color: #9ca3af;
}

.notification-history-message {
  font-size: 0.875rem;
  color: #4b5563;
  margin: 0;
  line-height: 1.5;
}

.dark .notification-history-message {
  color: #d1d5db;
}

.notification-history-actions {
  display: flex;
  justify-content: space-between;
  margin-top: 0.75rem;
}

.notification-mark-read {
  font-size: 0.75rem;
  color: #6b7280;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
}

.notification-mark-read:hover {
  color: #111827;
}

.dark .notification-mark-read:hover {
  color: white;
}

/* Modal de mensagem */
.notification-message-modal {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
}

.notification-message-content {
  position: relative;
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  width: 100%;
  max-width: 32rem;
  margin: 1rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.dark .notification-message-content {
  background-color: #1f2937;
}

.notification-message-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.dark .notification-message-header {
  border-color: rgba(255, 255, 255, 0.1);
}

.notification-message-title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
}

.dark .notification-message-title {
  color: white;
}

.notification-message-body {
  padding: 1rem;
  font-size: 0.875rem;
  color: #4b5563;
  line-height: 1.5;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  max-height: 60vh;
  overflow-y: auto;
}

.dark .notification-message-body {
  color: #d1d5db;
}

.notification-message-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background-color: #f9fafb;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
}

.dark .notification-message-footer {
  background-color: #1f2937;
  border-color: rgba(255, 255, 255, 0.1);
}

.notification-message-time {
  font-size: 0.75rem;
  color: #6b7280;
}

.dark .notification-message-time {
  color: #9ca3af;
}

.notification-message-type {
  display: flex;
  align-items: center;
  font-size: 0.75rem;
  color: #6b7280;
}

.dark .notification-message-type {
  color: #9ca3af;
}

.notification-panel-overlay {
  position: fixed;
  inset: 0;
  z-index: 40;
}

/* Animações */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-0.625rem);
}

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

/* Responsividade para dispositivos móveis */
@media (max-width: 640px) {
  .notification-history-content {
    max-width: none;
    max-height: 100vh;
    margin: 0;
    border-radius: 0;
  }
  
  .notification-message-content {
    max-width: none;
    margin: 1rem;
    max-height: calc(100vh - 2rem);
  }
  
  .notification-history-body {
    padding: 0.75rem;
  }
  
  .notification-filters {
    margin-bottom: 1rem;
  }
  
  .notification-filter-controls {
    flex-wrap: wrap;
  }

  
  .notification-filter-select {
    flex: 1;
    min-width: 7.5rem;
  }
  .notification-bell-button.notification-disabled {
  background-color: #fee2e2; /* Vermelho muito claro */
  border: 1px solid #dc2626; /* Vermelho forte */
  color: #dc2626; /* Sino vermelho */
  cursor: not-allowed;
}

.notification-bell-button.notification-disabled:hover {
  background-color: #fee2e2; /* Não muda ao passar mouse */
}

}

.notification-mark-read-btn {
  font-size: 0.875rem;
  color: #3b82f6;
  background: none;
  border: none;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

.notification-mark-read-btn:hover {
  background-color: #e0e7ff;
}

/* Botão menor e mais discreto */
.notification-mark-read-btn-small {
  font-size: 0.70rem;
  padding: 0.1rem 0.4rem;
  border-radius: 0.2rem;
  margin-left: 0.5rem;
}

.notification-bell-loading {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  margin: auto;
  width: 1.5rem;
  height: 1.5rem;
  color: #3b82f6;
  animation: spin 1s linear infinite;
  z-index: 10;
  pointer-events: none;
  opacity: 0.8;
}
@keyframes spin {
  from { transform: rotate(0deg);}
  to { transform: rotate(360deg);}
}
</style>

